/**
 * Realtor Contacts Book (#332, PRD #220 §14.8).
 *
 * The realtor's private address book: buyers, sellers, owners, builders and
 * contractors. Search and the type filter run on the server (`q`, `type`);
 * the chip counts come from the same response and cover the current search
 * across every type, so a chip shows what choosing it would list.
 *
 * Add and Edit share one dialog. Delete asks first: the server removes the
 * contact and its message thread but keeps its leads.
 *
 * While WhatsApp chats wait in the review list, the header links to it with
 * their count.
 */

import {
	FileUp,
	LayoutGrid,
	List,
	Loader2,
	MessagesSquare,
	Plus,
	RefreshCw,
	Search,
	SearchX,
	Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
	ContactEntry,
	type ContactView,
} from "@/features/realtor/components/contacts/contact-entry";
import { ContactFormDialog } from "@/features/realtor/components/contacts/contact-form-dialog";
import { DeleteContactDialog } from "@/features/realtor/components/contacts/delete-contact-dialog";
import { ImportChatDialog } from "@/features/realtor/components/import/import-chat-dialog";
import { WHATSAPP_REVIEW_PATH } from "@/features/realtor/components/settings/whatsapp-connection-card";
import { useContacts } from "@/features/realtor/hooks/use-realtor-crm";
import { useWhatsappCapture } from "@/features/realtor/hooks/use-whatsapp";
import {
	CONTACT_TYPES,
	type Contact,
	type ContactCounts,
	type ContactSummary,
	type ContactType,
} from "@/features/realtor/services/realtor-crm-api.service";
import { StudioButton } from "@/features/studio/theme";

const SEARCH_DEBOUNCE_MS = 250;

type FormState = { mode: "create" } | { mode: "edit"; contact: Contact } | null;

export function RealtorContactsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [search, setSearch] = useState("");
	const [query, setQuery] = useState("");
	const [type, setType] = useState<ContactType | undefined>(undefined);
	const [view, setView] = useState<ContactView>("cards");
	const [form, setForm] = useState<FormState>(null);
	const [deleting, setDeleting] = useState<ContactSummary | null>(null);
	const [importing, setImporting] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [search]);

	const contactsQuery = useContacts({ q: query || undefined, type });
	const data = contactsQuery.data;
	// While a new search loads, the previous result stays on screen. An empty
	// previous result would claim "no matches" for a search not yet run, so
	// that case shows the skeleton instead.
	const previousWasEmpty =
		contactsQuery.isPlaceholderData && data?.contacts.length === 0;

	const clearFilters = () => {
		setSearch("");
		setQuery("");
		setType(undefined);
	};

	const openMessages = (contactId: string) =>
		navigate(`/realtor/messages?contact=${encodeURIComponent(contactId)}`);

	return (
		<div className="bg-dashboard-surface min-h-screen space-y-6 p-4 sm:p-6">
			<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between sm:p-6">
					<div className="min-w-0">
						<p className="text-xs font-mono uppercase tracking-widest text-dashboard-muted">
							{t("realtor.contacts.eyebrow")}
						</p>
						<h1
							className="font-display text-3xl italic font-medium text-dashboard-text mt-2"
							style={{ lineHeight: 1.1 }}
						>
							{t("realtor.contacts.titleLead")}{" "}
							<span className="text-dashboard-accent-display">
								{t("realtor.contacts.titleAccent")}
							</span>
						</h1>
						<p className="text-sm text-dashboard-muted mt-2">
							{t("realtor.contacts.subtitle")}
						</p>
						<WhatsappReviewLink />
					</div>
					<div className="flex shrink-0 flex-wrap gap-2 self-start">
						<StudioButton
							variant="secondary"
							size="sm"
							className="gap-1.5"
							onClick={() => setImporting(true)}
						>
							<FileUp className="h-4 w-4" />
							{t("realtor.import.action")}
						</StudioButton>
						<StudioButton
							size="sm"
							className="gap-1.5"
							onClick={() => setForm({ mode: "create" })}
						>
							<Plus className="h-4 w-4" />
							{t("realtor.contacts.addCta")}
						</StudioButton>
					</div>
				</CardContent>
			</Card>

			<div className="space-y-3">
				<div className="flex items-center gap-2">
					<div className="relative min-w-0 flex-1">
						<Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-dashboard-muted" />
						<Input
							type="search"
							aria-label={t("realtor.contacts.searchLabel")}
							placeholder={t("realtor.contacts.searchPlaceholder")}
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							maxLength={200}
							className="h-10 rounded-lg border-dashboard-border bg-dashboard-bg pl-10 pr-9 text-sm shadow-sm"
						/>
						{contactsQuery.isFetching && !contactsQuery.isPending && (
							<Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-dashboard-muted" />
						)}
					</div>
					<ViewToggle view={view} onChange={setView} />
				</div>
				<TypeChips counts={data?.counts} selected={type} onSelect={setType} />
			</div>

			{contactsQuery.isError ? (
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="space-y-3 p-6 text-center" role="alert">
						<p className="text-sm font-medium text-dashboard-text">
							{t("realtor.contacts.loadFailedTitle")}
						</p>
						<p className="text-sm text-dashboard-muted">
							{contactsQuery.error.message}
						</p>
						<StudioButton
							variant="secondary"
							size="sm"
							className="gap-1.5"
							disabled={contactsQuery.isFetching}
							onClick={() => contactsQuery.refetch()}
						>
							<RefreshCw className="h-4 w-4" />
							{t("realtor.contacts.retry")}
						</StudioButton>
					</CardContent>
				</Card>
			) : !data || previousWasEmpty ? (
				<div
					className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
					data-testid="contacts-loading"
				>
					{[0, 1, 2].map((key) => (
						<Skeleton
							key={key}
							className="h-40 w-full rounded-xl bg-dashboard-bg"
						/>
					))}
				</div>
			) : data.counts.all === 0 && !query ? (
				<EmptyState
					icon={<Users className="h-5 w-5 text-dashboard-accent-dark" />}
					title={t("realtor.contacts.emptyTitle")}
					body={t("realtor.contacts.emptyBody")}
					action={
						<StudioButton
							size="sm"
							className="gap-1.5"
							onClick={() => setForm({ mode: "create" })}
						>
							<Plus className="h-4 w-4" />
							{t("realtor.contacts.addCta")}
						</StudioButton>
					}
				/>
			) : data.contacts.length === 0 ? (
				<EmptyState
					icon={<SearchX className="h-5 w-5 text-dashboard-accent-dark" />}
					title={t("realtor.contacts.noMatchTitle")}
					body={t("realtor.contacts.noMatchBody")}
					action={
						<StudioButton variant="secondary" size="sm" onClick={clearFilters}>
							{t("realtor.contacts.clearFilters")}
						</StudioButton>
					}
				/>
			) : (
				<ul
					data-view={view}
					className={
						view === "cards"
							? "grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
							: "divide-y divide-dashboard-border overflow-hidden rounded-xl border border-dashboard-border bg-dashboard-bg shadow-[var(--dashboard-shadow-2)]"
					}
				>
					{data.contacts.map((summary) => (
						<ContactEntry
							key={summary.contact.id}
							summary={summary}
							view={view}
							onMessage={openMessages}
							onEdit={(s) => setForm({ mode: "edit", contact: s.contact })}
							onDelete={setDeleting}
						/>
					))}
				</ul>
			)}

			{form && (
				<ContactFormDialog
					contact={form.mode === "edit" ? form.contact : undefined}
					defaultType={type}
					onClose={() => setForm(null)}
				/>
			)}
			{deleting && (
				<DeleteContactDialog
					summary={deleting}
					onClose={() => setDeleting(null)}
				/>
			)}
			<ImportChatDialog open={importing} onOpenChange={setImporting} />
		</div>
	);
}

function TypeChips({
	counts,
	selected,
	onSelect,
}: {
	counts: ContactCounts | undefined;
	selected: ContactType | undefined;
	onSelect: (type: ContactType | undefined) => void;
}) {
	const { t } = useTranslation();
	const options: Array<{ value: ContactType | undefined; label: string }> = [
		{ value: undefined, label: t("realtor.contacts.all") },
		...CONTACT_TYPES.map((value) => ({
			value,
			label: t(`realtor.contacts.types.${value}`),
		})),
	];
	return (
		<fieldset className="m-0 flex min-w-0 flex-wrap gap-2 border-0 p-0">
			<legend className="sr-only">{t("realtor.contacts.filterLabel")}</legend>
			{options.map(({ value, label }) => {
				const active = selected === value;
				const count = counts?.[value ?? "all"];
				return (
					<button
						key={value ?? "all"}
						type="button"
						aria-pressed={active}
						onClick={() => onSelect(value)}
						className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors ${
							active
								? "border-transparent bg-dashboard-accent text-dashboard-on-fill"
								: "border-dashboard-border bg-dashboard-bg text-dashboard-text hover:bg-dashboard-surface"
						}`}
					>
						{label}
						{count !== undefined && " "}
						{count !== undefined && (
							<span
								className={`tabular-nums ${active ? "text-dashboard-on-fill/80" : "text-dashboard-muted"}`}
							>
								{count}
							</span>
						)}
					</button>
				);
			})}
		</fieldset>
	);
}

function ViewToggle({
	view,
	onChange,
}: {
	view: ContactView;
	onChange: (view: ContactView) => void;
}) {
	const { t } = useTranslation();
	const options: Array<{
		value: ContactView;
		label: string;
		icon: React.ReactNode;
	}> = [
		{
			value: "cards",
			label: t("realtor.contacts.viewCards"),
			icon: <LayoutGrid className="h-4 w-4" />,
		},
		{
			value: "list",
			label: t("realtor.contacts.viewList"),
			icon: <List className="h-4 w-4" />,
		},
	];
	return (
		<fieldset className="m-0 flex shrink-0 rounded-lg border border-dashboard-border bg-dashboard-bg p-0.5">
			<legend className="sr-only">{t("realtor.contacts.viewLabel")}</legend>
			{options.map(({ value, label, icon }) => (
				<button
					key={value}
					type="button"
					aria-pressed={view === value}
					aria-label={label}
					title={label}
					onClick={() => onChange(value)}
					className={`inline-flex h-8 w-9 items-center justify-center rounded-md transition-colors ${
						view === value
							? "bg-dashboard-surface text-dashboard-accent-dark"
							: "text-dashboard-muted hover:text-dashboard-text"
					}`}
				>
					{icon}
				</button>
			))}
		</fieldset>
	);
}

function EmptyState({
	icon,
	title,
	body,
	action,
}: {
	icon: React.ReactNode;
	title: string;
	body: string;
	action: React.ReactNode;
}) {
	return (
		<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
			<CardContent className="space-y-3 p-8 text-center">
				<div className="flex justify-center">{icon}</div>
				<div className="space-y-1">
					<h2 className="font-display text-base italic font-medium text-dashboard-text">
						{title}
					</h2>
					<p className="mx-auto max-w-sm text-xs text-dashboard-muted">
						{body}
					</p>
				</div>
				<div className="flex justify-center">{action}</div>
			</CardContent>
		</Card>
	);
}

/**
 * "12 WhatsApp chats to review", linking to the review page. Shown only
 * where the settings card is and while at least one chat waits.
 */
function WhatsappReviewLink() {
	const { t } = useTranslation();
	const { connection } = useWhatsappCapture();
	const waiting = connection?.reviewEntriesLeft ?? 0;
	if (waiting <= 0) return null;
	return (
		<Link
			to={WHATSAPP_REVIEW_PATH}
			className="mt-2 inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-dashboard-accent-dark underline md:min-h-0"
		>
			<MessagesSquare className="h-4 w-4" />
			{t("realtor.contacts.whatsappReview", { count: waiting })}
		</Link>
	);
}
